import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Step, StepList, StepPanel, Steps } from '../CommonComponents/OPL/Stepper';
import { ScrollHereOnce } from '../CommonComponents/ScrollHereOnce';
import useScript from '../_hooks/useScript';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { Card } from '../CommonComponents/OPL/Card/Card';
import { StickyFooter } from '../CommonComponents/OPL/StickyFooter/StickyFooter';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { useResetModuleWrapperStyles } from '../_hooks/useResetModuleWrapperStyles';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { selectComponentProps } from '../ConsumerArea/ConsumerAreaSelector';
import { ApiService, ComponentProps, UserConfig } from './CustomFormsWorkflow.meta';
import { CustomFormsComponent } from './components/CustomFormsComponent/CustomFormsComponent';
import { FullPageLoading } from '../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Content } from './CustomFormsWorkflow.styles';
import { ReviewPage } from './components/ReviewPage/ReviewPage';
import { logout } from '../Admin/utils/logout';

export const CustomFormsWorkflow = () => {
  useResetModuleWrapperStyles();

  const componentProps: ComponentProps = useSelector(selectComponentProps);

  const [selectedStepIndex, setSelectedStepIndex] = useState(0);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [apiService, setApiService] = useState<ApiService | null>(null);

  const [isScriptLoaded, isScriptError] = useScript(componentProps.ComponentPath, {
    isModule: true,
  });

  if (!isScriptLoaded) {
    return (
      <>
        <Box style={{ height: '100vh' }}>
          <FullPageLoading text="Loading" />
        </Box>

        <StickyFooter />
      </>
    );
  }

  if (isScriptError) {
    return (
      <>
        <Box style={{ height: '100vh' }}>
          <ErrorState
            height="100%"
            message="There was an error loading the page. Please try again later."
          />
        </Box>

        <StickyFooter />
      </>
    );
  }

  const isFinalStep = selectedStepIndex === componentProps.Forms.length;

  const userConfig: UserConfig = {
    oasToken: componentProps.UserConfig.OasToken,
    viewType: componentProps.UserConfig.ViewType,
    env: componentProps.UserConfig.Env,
  };

  const onFormReady = (evtDetail: { service: ApiService }) => {
    setApiService(evtDetail.service);
  };

  const submitForm = async () => {
    if (apiService) {
      setIsSubmitting(true);

      const resp = await apiService.save();

      setIsSubmitting(false);

      if (resp && !resp.hasErrors) {
        setSelectedStepIndex(selectedStepIndex + 1);
      }
    }
  };

  return (
    <>
      <ScrollHereOnce key={selectedStepIndex} options={false} />

      <Card maxWidth={1080} mx="auto" mt="48" p="24">
        <Text as="h1" fontSize="h4" fontWeight={500} mb="8">
          {isFinalStep ? 'Acknowledgement and Signature' : componentProps.Title}
        </Text>

        <Steps selectedIndex={selectedStepIndex} onSelect={index => setSelectedStepIndex(index)}>
          <StepList>
            {componentProps.Forms.map((form, index) => (
              <Step key={form.Id} disabled={selectedStepIndex < index}>
                {componentProps.Forms[index]?.Name}
              </Step>
            ))}

            <Step>Review</Step>
          </StepList>

          <Content mt="24">
            {componentProps.Forms.map(form => (
              <StepPanel key={form.Id}>
                <CustomFormsComponent
                  userConfig={userConfig}
                  formSessionId={form.Id}
                  onFormReady={onFormReady}
                />
              </StepPanel>
            ))}

            <StepPanel>
              <ReviewPage />
            </StepPanel>
          </Content>
        </Steps>
      </Card>

      <StickyFooter>
        {selectedStepIndex > 0 && (
          <Button
            variant="secondary"
            leftIcon="fas fa-chevron-left"
            size="large"
            onClick={() => setSelectedStepIndex(selectedStepIndex - 1)}
            mr="auto"
          >
            Previous
          </Button>
        )}

        {!isFinalStep && (
          <Button
            rightIcon="fas fa-chevron-right"
            size="large"
            onClick={submitForm}
            isLoading={isSubmitting}
            disabled={!apiService}
            ml="auto"
          >
            Next
          </Button>
        )}

        {isFinalStep && (
          <Button
            size="large"
            onClick={() => {
              logout(true);
            }}
            ml="auto"
          >
            Done
          </Button>
        )}
      </StickyFooter>
    </>
  );
};
